import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAiAvailability } from '@/features/ai-assist/useAiAvailability';
import { openCopilot } from '@/features/copilot/api';
import { CornerDownLeft, FileText, Receipt, Search, UserRound } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/Dialog';
import { toneBadgeClass } from '@/components/ui/KpiTile';
import { globalSearch } from '@/api';
import { cn } from '@/lib/cn';
import { actionsFor, shortcutLabel } from './actions';
import { findSection, settingMatches, settingPath, visibleAdminSettings } from '@/features/admin/adminRegistry';

interface SearchPatient { id: number; uhid: string; name: string; gender: string; age: number; mobile?: string }
interface SearchVisit { id: number; visit_id: string; patient_name: string; patient_uhid: string; current_stage: string }
interface SearchBill { id: number; bill_number: string; patient_name: string; total_amount: string | number; payment_status: string }
interface SearchResults { patients: SearchPatient[]; visits: SearchVisit[]; bills: SearchBill[] }

type Group = 'Actions' | 'Settings' | 'Patients' | 'Visits' | 'Bills' | 'Copilot';
interface Item { key: string; group: Group; title: string; sub: string; icon: ReactNode; shortcut?: string; run: () => void }

export interface CommandPaletteProps {
  /** The signed-in role's modules — decides which actions are offered. */
  modules: readonly string[] | undefined;
  /** The signed-in role — a few Admin settings are shown by role, not grant. */
  role?: string;
  onNavigate: (mod: string) => void;
  /** Opens an Admin Panel setting by its path (/admin/<section>/<setting>). */
  onNavigateToSetting?: (path: string) => void;
  onNavigateToPatient: (uhid: string) => void;
  onNavigateToVisit: (visitId: string) => void;
  onNavigateToBill: (billId: number) => void;
  /** Phone topbar: an icon-only trigger instead of the 300px search field. */
  compact?: boolean;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const NEUTRAL_ICON = 'bg-tone-slate-soft text-tone-slate';

/**
 * The command palette (QuickActions_Options.html, option C) — replaces the
 * topbar's old inline search box. Ctrl+K (⌘K on a Mac) or a click on the
 * search field opens it from any screen. One list: matching Quick Actions
 * first, then the patients, visits and bills the existing GET
 * /api/search/global returns. Arrow keys move, Enter opens, Esc closes —
 * nothing needs the mouse. With nothing typed it lists the actions.
 */
export function CommandPalette({ modules, role, onNavigate, onNavigateToSetting, onNavigateToPatient, onNavigateToVisit, onNavigateToBill, compact = false }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const { aiEnabled } = useAiAvailability();
  const copilotOn = !!modules?.includes('copilot') && aiEnabled;
  const [results, setResults] = useState<SearchResults | null>(null);
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Ctrl+K / ⌘K from anywhere toggles the palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((isMac ? e.metaKey : e.ctrlKey) && !e.altKey && !e.shiftKey && e.code === 'KeyK') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) { setQ(''); setResults(null); setSearching(false); }
  }, [open]);

  // Record search: debounced, only once something is typed.
  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults(null); setSearching(false); return; }
    setSearching(true);
    let alive = true;
    const t = setTimeout(() => {
      globalSearch(term)
        .then((r: SearchResults) => { if (alive) setResults(r); })
        .catch(() => { if (alive) setResults({ patients: [], visits: [], bills: [] }); })
        .finally(() => { if (alive) setSearching(false); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const close = useCallback(() => setOpen(false), []);

  const items = useMemo<Item[]>(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const actions = actionsFor(modules).filter(a => {
      const hay = `${a.label} ${a.keywords}`.toLowerCase();
      return words.every(w => hay.includes(w));
    });
    const out: Item[] = actions.map(a => {
      const Icon = a.icon;
      return {
        key: `a-${a.id}`, group: 'Actions', title: a.label, sub: a.hint, shortcut: shortcutLabel(a.shortcut),
        icon: <span className={cn('flex h-7 w-7 items-center justify-center rounded-md', toneBadgeClass(a.tone))}><Icon className="h-4 w-4" /></span>,
        run: () => { close(); onNavigate(a.mod); },
      };
    });
    // Admin settings, only once something is typed — with an empty query the
    // palette lists Quick Actions, and 26 settings would bury them.
    if (q.trim() && onNavigateToSetting && modules?.includes('admin-panel')) {
      visibleAdminSettings(modules, role).filter(s => settingMatches(s, q)).slice(0, 6).forEach(s => {
        const Icon = s.icon;
        out.push({
          key: `s-${s.id}`, group: 'Settings', title: s.label, sub: `Admin · ${findSection(s.section)?.label ?? ''}`,
          icon: <span className={cn('flex h-7 w-7 items-center justify-center rounded-md', NEUTRAL_ICON)}><Icon className="h-4 w-4" /></span>,
          run: () => { close(); onNavigateToSetting(settingPath(s)); },
        });
      });
    }
    if (results) {
      results.patients.forEach(p => out.push({
        key: `p-${p.id}`, group: 'Patients', title: p.name,
        sub: `${p.uhid} · ${p.gender}${p.age != null ? `, ${p.age}y` : ''}${p.mobile ? ` · ${p.mobile}` : ''}`,
        icon: <span className={cn('flex h-7 w-7 items-center justify-center rounded-md', NEUTRAL_ICON)}><UserRound className="h-4 w-4" /></span>,
        run: () => { close(); onNavigateToPatient(p.uhid); },
      }));
      results.visits.forEach(v => out.push({
        key: `v-${v.id}`, group: 'Visits', title: v.visit_id, sub: `${v.patient_name} · ${v.patient_uhid} · ${v.current_stage}`,
        icon: <span className={cn('flex h-7 w-7 items-center justify-center rounded-md', NEUTRAL_ICON)}><FileText className="h-4 w-4" /></span>,
        run: () => { close(); onNavigateToVisit(v.visit_id); },
      }));
      results.bills.forEach(b => out.push({
        key: `b-${b.id}`, group: 'Bills', title: b.bill_number,
        sub: `${b.patient_name} · ₹${parseFloat(String(b.total_amount)).toFixed(2)} · ${b.payment_status}`,
        icon: <span className={cn('flex h-7 w-7 items-center justify-center rounded-md', NEUTRAL_ICON)}><Receipt className="h-4 w-4" /></span>,
        run: () => { close(); onNavigateToBill(b.id); },
      }));
    }
    // G2: anything typed can be handed to ClintoBytes Copilot as a question.
    if (q.trim().length >= 3 && copilotOn) {
      out.push({
        key: 'copilot-ask', group: 'Copilot', title: `Ask Copilot: “${q.trim()}”`, sub: 'Answers with your own permissions',
        icon: <img src="/clintobytes-copilot-56.webp" srcSet="/clintobytes-copilot-112.webp 2x" alt="" className="h-7 w-7 rounded-full" />,
        run: () => { close(); openCopilot(q.trim()); },
      });
    }
    return out;
  }, [q, results, modules, role, close, copilotOn, onNavigate, onNavigateToSetting, onNavigateToPatient, onNavigateToVisit, onNavigateToBill]);

  useEffect(() => { setActive(0); }, [items.length, q]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(i => (items.length ? (i + 1) % items.length : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => (items.length ? (i - 1 + items.length) % items.length : 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); items[active]?.run(); }
  };

  const hotkey = isMac ? '⌘K' : 'Ctrl K';
  let lastGroup: Group | null = null;

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Search patients, visits and bills"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-control-line bg-card text-ink-muted transition-colors hover:bg-muted/40"
        >
          <Search className="h-4 w-4" />
        </button>
      ) : (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Search or run an action (${hotkey})`}
        className="flex h-9 w-[300px] shrink-0 items-center gap-2 rounded-lg border border-control-line bg-card px-3 text-left text-[12.5px] text-ink-muted transition-colors hover:bg-muted/40"
      >
        <Search className="h-4 w-4 shrink-0 text-ink-muted" />
        <span className="flex-1 truncate">Search patient / UHID / Visit ID…</span>
        <kbd className="rounded border border-b-2 border-control-line bg-card px-1.5 font-sans text-[10px] text-ink-muted">{hotkey}</kbd>
      </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="top-[12%] w-[calc(100vw-16px)] max-w-[580px] translate-y-0 gap-0 overflow-hidden p-0 data-[state=closed]:slide-out-to-top-[10%] data-[state=open]:slide-in-from-top-[10%] [&>button:last-child]:hidden">
          <DialogTitle className="sr-only">Search or run an action</DialogTitle>
          <DialogDescription className="sr-only">Type to find a patient, visit or bill, or an action to run. Use the arrow keys and Enter.</DialogDescription>
          <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
            <Search className="h-[18px] w-[18px] shrink-0 text-ink-muted" />
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={onInputKey}
              placeholder="Search patients, visits, bills — or type an action or setting"
              aria-label="Search or run an action"
              className="h-7 flex-1 border-0 bg-transparent p-0 text-[14px] text-ink shadow-none outline-none focus:ring-0"
            />
            {searching && <span className="text-[11px] text-ink-muted">Searching…</span>}
            <kbd className="rounded border border-b-2 border-control-line bg-card px-1.5 font-sans text-[10px] text-ink-muted">Esc</kbd>
          </div>

          <div ref={listRef} role="listbox" aria-label="Results" className="max-h-[56vh] overflow-y-auto py-1">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px] text-ink-muted">
                {searching ? 'Searching…' : `No matches for “${q.trim()}”`}
              </div>
            ) : items.map((it, i) => {
              const header = it.group !== lastGroup ? it.group : null;
              lastGroup = it.group;
              return (
                <div key={it.key}>
                  {header && <div className="px-4 pb-1 pt-2.5 text-[10px] font-bold uppercase tracking-[.05em] text-ink-muted">{header}</div>}
                  <div
                    role="option"
                    aria-selected={i === active}
                    data-index={i}
                    onMouseMove={() => setActive(i)}
                    onClick={it.run}
                    className={cn('flex cursor-pointer items-center gap-3 px-4 py-2', i === active && 'bg-info-soft/70 shadow-[inset_3px_0_0_var(--teal)]')}
                  >
                    {it.icon}
                    <div className="min-w-0 flex-1">
                      <div className={cn('truncate text-[13px] font-medium text-ink', it.group === 'Visits' || it.group === 'Bills' ? 'font-mono' : '')}>{it.title}</div>
                      <div className="truncate text-[11px] text-ink-muted">{it.sub}</div>
                    </div>
                    {it.shortcut && <kbd className="rounded border border-b-2 border-control-line bg-card px-1.5 font-sans text-[10px] text-ink-muted">{it.shortcut}</kbd>}
                    {i === active && <CornerDownLeft aria-hidden className="h-3.5 w-3.5 text-ink-muted" />}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-4 border-t border-line bg-muted/50 px-4 py-2 text-[11px] text-ink-muted">
            <span><kbd className="font-sans">↑</kbd> <kbd className="font-sans">↓</kbd> move</span>
            <span><kbd className="font-sans">Enter</kbd> open</span>
            <span><kbd className="font-sans">Esc</kbd> close</span>
            <span className="ml-auto">Actions · Patients · Visits · Bills</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
